/* eslint-disable no-magic-numbers */

import { ForecastListItem } from '@futrli-sdk/predict';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';

import { defaultColors } from './constants';

/**
 * generate rgba code by adding opacity to color
 */
export const addOpacityToColor = (color: string, opacity = 0.2) => {
  if (opacity === 1 || color === 'transparent') {
    return color;
  }
  if (color[0] === '#') {
    const rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    if (!rgb) {
      return color;
    }
    return `rgb(${parseInt(rgb[1], 16)}, ${parseInt(rgb[2], 16)}, ${parseInt(rgb[3], 16)})`
      .replace('rgb', 'rgba')
      .replace(')', `, ${opacity})`);
  }
};

// lighten or darken default colors by up to 50% depending on how many fields are there
export const generateDefaultColors = (n: number) => {
  if (n <= defaultColors.length) {
    return defaultColors;
  } else {
    const colors = [...defaultColors];
    const cn = n - defaultColors.length;
    const mStep = Math.ceil(cn / defaultColors.length);
    const s = 1 / (Math.floor((mStep + 1) / 2) + 1);
    const steps = [];
    for (let i = 0; i < mStep; i++) {
      steps.push(1 + (i % 2 ? 1 : -1) * s * 0.5 * Math.ceil((i + 1) / 2));
    }

    for (let i = 0; i < cn; i++) {
      const index = i % defaultColors.length;
      const multip = Math.floor(i / defaultColors.length);
      colors.push(shadeColor(defaultColors[index], steps[multip]));
    }
    return colors;
  }
};

export const shadeColor = (color: string, decimal: number): string => {
  const base = color.startsWith('#') ? 1 : 0;

  let r = parseInt(color.substring(base, 3), 16);
  let g = parseInt(color.substring(base + 2, 5), 16);
  let b = parseInt(color.substring(base + 4, 7), 16);

  r = Math.round(r / decimal);
  g = Math.round(g / decimal);
  b = Math.round(b / decimal);

  r = r < 255 ? r : 255;
  g = g < 255 ? g : 255;
  b = b < 255 ? b : 255;

  const rr = r.toString(16).length === 1 ? `0${r.toString(16)}` : r.toString(16);
  const gg = g.toString(16).length === 1 ? `0${g.toString(16)}` : g.toString(16);
  const bb = b.toString(16).length === 1 ? `0${b.toString(16)}` : b.toString(16);

  return `#${rr}${gg}${bb}`;
};

/**
 * calculate and returns the right color for a field on a chart
 */
export const getColorForChartData = (
  c: string[] | Record<string, string> | undefined,
  ci: number,
  fdi: number,
  chartColors: string[],
  darkningStep: number,
  selectedForecasts: (ForecastListItem | ConsolidatedForecastListItem)[]
): string => {
  if (!c) {
    return shadeColor(chartColors[ci], 1 + fdi * darkningStep);
  }
  if (Array.isArray(c)) {
    return shadeColor(c[0] || chartColors[ci], 1 + fdi * darkningStep);
  } else {
    return c[selectedForecasts[fdi].id] || shadeColor(chartColors[ci], 1 + fdi * darkningStep);
  }
};

/**
 * returns readable text color based on background color
 * @param bgColor color in hex format
 * @returns font color #000 or #fff
 */
export const getTextColorBasedOnBackground = (bgColor: string) => {
  // Convert hex to RGB
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black for light background, white for dark background
  return luminance > 0.3 ? '#000' : '#fff';
};
